import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BatchSelectionResponse } from "@src/core/engine/workers/batchSelection/schema";
import { BuildSuggestionOutput } from "@src/routes/projects/$projectId/selections/hooks/useBuildSuggestionOutput";
import { GetGroupedProductDescriptions } from "@src/shared/components/ProductDetailsCard/hooks/useGetProductDescriptions";
import { isNotNil } from "@src/shared/helpers/guards";
import { listify, objectify, set } from "radashi";
import { buildBatchFormattedSuggestion } from "./helpers/buildBatchFormattedSuggestion";
import { ensureDataState } from "./helpers/ensureDataState";
import { getEmptyRow } from "./helpers/getEmptyRow";
import { markErrored } from "./helpers/markErrored";
import { initPasteCellValue, pasteCellValues } from "./pasteCellValues";
import { initReloadAllValues, reloadAllValues } from "./reloadAllValues";
import { initReplaceAllValues, replaceAllValues } from "./replaceAllValues";
import {
  BatchFormattedSuggestion,
  BatchSelectionRow,
  SubProductsSelection,
} from "./types";
import { initUpdateCellValue, updateCellValue } from "./updateCellValue";

export type BatchSelectionState = {
  data: BatchSelectionRow[];
};

const batchSelectionInitialState: BatchSelectionState = {
  data: [getEmptyRow()],
};

export const batchSelectionSlice = createSlice({
  extraReducers: (builder) => {
    builder
      // Update single cell value
      .addCase(updateCellValue.pending, (state, action) => {
        initUpdateCellValue(state, action.meta.arg, action.meta.requestId);
      })
      .addCase(updateCellValue.fulfilled, (state, action) => {
        applyResults(
          state,
          action.payload,
          action.meta.requestId,
          action.meta.arg.buildSuggestionOutput,
          action.meta.arg.getProductDescriptions,
        );
      })
      .addCase(updateCellValue.rejected, (state, action) => {
        markErrored(state, action.meta.requestId);
      })

      // Paste cell values
      .addCase(pasteCellValues.pending, (state, action) => {
        initPasteCellValue(state, action.meta.arg, action.meta.requestId);
      })
      .addCase(pasteCellValues.fulfilled, (state, action) => {
        applyResults(
          state,
          action.payload,
          action.meta.requestId,
          action.meta.arg.buildSuggestionOutput,
          action.meta.arg.getProductDescriptions,
        );
      })
      .addCase(pasteCellValues.rejected, (state, action) => {
        markErrored(state, action.meta.requestId);
      })

      // Reload all values
      .addCase(reloadAllValues.pending, (state, action) => {
        initReloadAllValues(state, action.meta.arg, action.meta.requestId);
      })
      .addCase(reloadAllValues.fulfilled, (state, action) => {
        applyResults(
          state,
          action.payload,
          action.meta.requestId,
          action.meta.arg.buildSuggestionOutput,
          action.meta.arg.getProductDescriptions,
        );
      })
      .addCase(reloadAllValues.rejected, (state, action) => {
        markErrored(state, action.meta.requestId);
      })

      // Replace all values
      .addCase(replaceAllValues.pending, (state, action) => {
        initReplaceAllValues(state, action.meta.arg, action.meta.requestId);
      })
      .addCase(replaceAllValues.fulfilled, (state, action) => {
        applyResults(
          state,
          action.payload,
          action.meta.requestId,
          action.meta.arg.buildSuggestionOutput,
          action.meta.arg.getProductDescriptions,
        );
      })
      .addCase(replaceAllValues.rejected, (state, action) => {
        markErrored(state, action.meta.requestId);
      });
  },
  initialState: batchSelectionInitialState,
  name: "batchSelection",
  reducers: {
    deleteRow: (state, action: PayloadAction<{ index: number }>) => {
      const { index } = action.payload;
      const nextData = [...state.data];
      nextData.splice(index, 1);
      state.data = ensureDataState(nextData);
    },
    resetBatchSelection: (state) => {
      state.data = [getEmptyRow()];
    },
    setAlternativeSuggestion: (
      state,
      action: PayloadAction<{
        rowId: string;
        suggestion: BatchFormattedSuggestion;
      }>,
    ) => {
      const { rowId, suggestion } = action.payload;
      const rowIndex = state.data.findIndex((row) => row.id === rowId);
      if (rowIndex === -1) return;
      state.data[rowIndex].output = {
        ...state.data[rowIndex].output,
        subProductsSelection: null,
        suggestion,
      };
    },
    setSubProductsSelection: (
      state,
      action: PayloadAction<{
        rowId: string;
        subProductsSelection: SubProductsSelection | null;
      }>,
    ) => {
      const { rowId, subProductsSelection } = action.payload;
      const rowIndex = state.data.findIndex((row) => row.id === rowId);
      if (rowIndex === -1) return;
      const row = state.data[rowIndex];
      state.data[rowIndex] = set(
        row,
        "output.subProductsSelection",
        subProductsSelection,
      );
    },
  },
});

export const {
  deleteRow,
  resetBatchSelection,
  setAlternativeSuggestion,
  setSubProductsSelection,
} = batchSelectionSlice.actions;

function applyResults(
  state: BatchSelectionState,
  results: Omit<BatchSelectionResponse, "type">,
  correlationId: string,
  buildSuggestionOutput: BuildSuggestionOutput,
  getProductDescriptions: GetGroupedProductDescriptions,
) {
  const nextData = [...state.data];
  results.results.forEach((result) => {
    const rowIndex = nextData.findIndex((row) => row.id === result.rowId);
    if (rowIndex === -1) return;
    const row = nextData[rowIndex];
    if (row.output?.correlationId !== correlationId) return;
    const withStatus = set(row, "status", result.validationResult);
    if (
      result.validationResult &&
      result.validationResult?.severity === "error"
    ) {
      nextData[rowIndex] = set(withStatus, "output", {
        correlationId,
        pipeResult: null,
        status: "loaded",
        suggestion: null,
      });
      return;
    }
    nextData[rowIndex] = set<BatchSelectionRow, BatchSelectionRow["output"]>(
      withStatus,
      "output",
      {
        correlationId,
        pipeResult: result.pipeResults
          ? objectify(
              listify(result.pipeResults, (_, suggestionOutput) =>
                suggestionOutput
                  ? buildSuggestionOutput(suggestionOutput.translationKey, [
                      suggestionOutput,
                    ])
                  : null,
              ).filter(isNotNil),
              (value) => value.id,
              (value) => value,
            )
          : null,
        status: "loaded",
        subProductsSelection:
          nextData[rowIndex].output?.subProductsSelection ?? null,
        suggestion: buildBatchFormattedSuggestion(
          result.suggestion,
          buildSuggestionOutput,
          getProductDescriptions,
        ),
      },
    );
  });
  state.data = nextData;
}
